import React, { Component, LegacyRef } from 'react'
import * as d3 from "d3"
import { URL } from "app/config/api-config";
import { Theme } from "@bdfx/mergado-ui-kit";

import { DEFAULT_LINE_CAP, DEFAULT_LINE_JOIN, getSVGPathFromPoints, sortVerticies } from "shape-edit/forms/helpers";

import { DELETE_KEY } from "app/helpers/keymap";
import { CoordsType } from 'shape-entities/components/shape';
import { ClipMaskObject } from 'shape-edit/forms/edit-clip-mask-modal';
import { PathPoint } from 'shape-entities/types/PathShape';
import { AnyPoint } from 'app/types/model-types';

const { min, max, abs, sin, cos, PI, round, atan, atan2, ceil, floor, sqrt, pow } = Math

type HandleType = 'c' | 'c2' | 'm'

interface Props {
    maskObject: ClipMaskObject
    ref: LegacyRef<any>
    updatePoints?: Function
    fillColor?: string
    strokeColor?: string
    strokeWidth?: number
    dashArray?: string
    newPointAngle?: number
    transformX?: number
    transformY?: number
}

interface State {
    isNew: boolean
    activeKey: number
    activeHandleType: HandleType
    dx: number
    dy: number
    dcx: number
    dcy: number
    c: number
    s: number
    left: number
    top: number
    scaleX: number
    scaleY: number
}

class MoveablePoints extends Component<Props, State> {
    handleRefs: React.RefObject<SVGRectElement | SVGCircleElement>[] = []
    addPointRectRef: React.RefObject<SVGRectElement>
    rectRef: React.RefObject<SVGRectElement>

    constructor(props) {
        super(props)

        const alpha = -((props.newPointAngle || 0)) / 180 * PI

        this.state = {
            isNew: null,
            activeKey: null,
            activeHandleType: null,
            dx: 0,
            dy: 0,
            dcx: 0,
            dcy: 0,
            c: cos(alpha),
            s: sin(alpha),
            left: 0,
            top: 0,
            scaleX: 1,
            scaleY: 1,
        }

        this.addPointRectRef = React.createRef()
        this.rectRef = React.createRef()
    }

    getHandleRef = (handleType: HandleType, index: number): React.RefObject<any> => {
        const refString = `moveable-handle-${handleType}-${index}`
        if (typeof this.handleRefs[refString] === 'undefined') {
            this.handleRefs[refString] = React.createRef()
        }
        return this.handleRefs[refString]
    }

    bindEvents = (key: number) => {
        ['m', 'c', 'c2'].forEach((handleType: HandleType) => {
            const ref = this.getHandleRef(handleType, key).current
            if (ref && d3.select(ref)) {
                d3.select(ref)
                    .style('cursor', 'move')
                    .call(
                        d3.drag()
                            .filter((event) => !event.button)
                            .on("start", (event) => this.dragStarted(event, handleType, key))
                            .on("drag", this.dragged)
                            .on("end", this.dragEnded)
                    );
            }
        })
    }

    bindAllEvents = () => {
        const { maskObject } = this.props
        maskObject.points.forEach((point, key) => {
            this.bindEvents(key)
        })

        //bind events on rectangle in order to set cx, cy and x, y at once
        const el = this.addPointRectRef.current
        if (el) {
            const isBezier = (this.props.maskObject.type === 'bezier')
            d3.select(el)
                .call(
                    d3.drag()
                        .filter((event) => !event.button)
                        .on("start", (event) => this.dragStarted(event, isBezier ? 'c' : 'm', 0))
                        .on("drag", this.dragged)
                        .on("end", this.dragEnded)
                );
        }
    }

    componentDidMount() {
        document.addEventListener("keydown", this.handleKeyDown)
        this.bindAllEvents()
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.handleKeyDown)
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (JSON.stringify(this.props.maskObject.points) !== JSON.stringify(prevProps.maskObject.points)) {
            this.bindAllEvents()
        }

        if (this.props.newPointAngle && this.props.newPointAngle !== prevProps.newPointAngle) {
            const alpha = -((this.props.newPointAngle || 0)) / 180 * PI
            this.setState({
                c: cos(alpha),
                s: sin(alpha),
            })
        }
    }

    handleKeyDown = (event) => {
        const { activeKey } = this.state
        const { maskObject } = this.props

        //do not prevent default for
        if (event.target.type || event.ctrlKey || activeKey === null || typeof this.props.updatePoints === 'undefined') {
            return;
        }

        switch (event.keyCode) {
            case DELETE_KEY:
                event.preventDefault()
                let updatedPoints = [...maskObject.points]
                updatedPoints.splice(activeKey, 1)
                this.props.updatePoints(updatedPoints)
                break;
            default:
                break;
        }
    }

    dragStarted = (event, handleType: HandleType, key: number) => {
        const { maskObject } = this.props

        const el = this.rectRef.current
        const boundingClientRect = el.getBoundingClientRect()

        const isNew = event.sourceEvent.target === this.addPointRectRef.current     //drag from rectangle (event binded to rectangle not to points)

        const padding = maskObject.padding || 0

        const width = maskObject.width + 2 * padding
        const height = maskObject.height + 2 * padding

        const scaleX = width / boundingClientRect.width
        const scaleY = height / boundingClientRect.height

        const common = {
            activeHandleType: handleType,
            isNew: isNew,
            left: boundingClientRect.left,
            top: boundingClientRect.top,
            scaleX: scaleX,
            scaleY: scaleY,
        }

        if (isNew) {
            const isBezier = maskObject.type === 'bezier'

            const XPos = round(((event.sourceEvent.clientX - boundingClientRect.left) * scaleX - .5 * width))
            const YPos = round(((event.sourceEvent.clientY - boundingClientRect.top) * scaleY - .5 * height))

            const newPoint = this.rotateByAngle({ x: XPos, y: YPos })
            const newPointKey = this.addPoint({
                x: newPoint.x,
                y: newPoint.y,
                cx: isBezier ? newPoint.x : undefined,
                cy: isBezier ? newPoint.y : undefined,
            }, event.sourceEvent.ctrlKey)

            this.setState({
                ...common,
                activeKey: newPointKey,
                dx: 0,
                dy: 0,
                dcx: 0,
                dcy: 0,
            })
        } else {
            if (handleType === 'c' || handleType === 'c2') {
                this.setState({ ...common, dcx: 0, dcy: 0, activeKey: key })
            } else {
                this.setState({ ...common, dx: 0, dy: 0, activeKey: key })
            }
        }
    }

    dragged = (event) => {
        const { activeHandleType, scaleX, scaleY, left, top, activeKey } = this.state
        const { maskObject } = this.props

        //to avoid false event.dx and event.dy:
        if ((event.dx && abs(event.dx) > 100) || (event.dy && abs(event.dy) > 100)) {
            return;
        }

        const padding = maskObject.padding || 0

        const width = maskObject.width + 2 * padding
        const height = maskObject.height + 2 * padding

        const XPos = round(((event.sourceEvent.clientX - left) * scaleX - .5 * width))
        const YPos = round(((event.sourceEvent.clientY - top) * scaleY - .5 * height))

        const newPoint = this.rotateByAngle({ x: XPos, y: YPos })
        const currentPoint: PathPoint = { ...maskObject.points[activeKey] }

        switch (activeHandleType) {
            case 'c':
                this.setState({
                    dcx: (newPoint.x - currentPoint.cx),
                    dcy: (newPoint.y - currentPoint.cy)
                })
                break;
            case 'c2':
                this.setState({
                    dcx: -(newPoint.x - 2 * currentPoint.x + currentPoint.cx),
                    dcy: -(newPoint.y - 2 * currentPoint.y + currentPoint.cy),
                })
                break;
            case 'm':
            default:
                this.setState({
                    dx: (newPoint.x - currentPoint.x),
                    dy: (newPoint.y - currentPoint.y),
                    dcx: (newPoint.x - currentPoint.x),
                    dcy: (newPoint.y - currentPoint.y),
                })
                break;
        }

    }

    dragEnded = (event) => {
        const { maskObject } = this.props
        const { dx, dy, dcx, dcy, activeKey } = this.state

        const isBezier = maskObject.type === 'bezier'
        const point: PathPoint = maskObject.points[activeKey]

        let updatedPoints = [...maskObject.points]

        if (point) {
            updatedPoints[activeKey] = {
                ...point,
                x: round(point.x + dx),
                y: round(point.y + dy),
                cx: isBezier ? round(point.cx + dcx) : undefined,
                cy: isBezier ? round(point.cy + dcy) : undefined,
            }
        }

        this.props.updatePoints(updatedPoints)
        this.setState({ dx: 0, dy: 0, dcx: 0, dcy: 0, isNew: false })
    }

    rotateByAngle = (coords: CoordsType): CoordsType => {
        const { c, s } = this.state

        return {
            x: coords.x * c - coords.y * s,
            y: coords.x * s + coords.y * c,
        }
    }

    addPoint = (newPoint: PathPoint | CoordsType, isCtrlKey: boolean = false): number => {
        const { maskObject } = this.props
        let updatedPoints = maskObject.points ? sortVerticies([...maskObject.points, newPoint]) : [newPoint]

        if (isCtrlKey) {
            const newPointIndex = updatedPoints.indexOf(newPoint)
            const maxIndex = updatedPoints.length - 1

            if (maxIndex && newPointIndex) {
                const prevPoint = updatedPoints[(newPointIndex - 1) % maxIndex]
                if (abs(prevPoint.x - newPoint.x) > abs(prevPoint.y - newPoint.y)) {
                    updatedPoints[newPointIndex] = { ...newPoint, y: prevPoint.y, cy: prevPoint?.cy || undefined }
                } else {
                    updatedPoints[newPointIndex] = { ...newPoint, x: prevPoint.x, cx: prevPoint?.cx || undefined }
                }
            }
        }

        //find the new key by coords:
        let currentPointKey = 0
        updatedPoints.forEach((point: CoordsType, key: number) => {
            if (point.x === newPoint.x && point.y === newPoint.y) {
                currentPointKey = key
            }
        })

        this.props.updatePoints(updatedPoints)
        this.setState({ activeKey: currentPointKey })
        return currentPointKey
    }

    render() {
        const { maskObject, fillColor, strokeColor, strokeWidth, dashArray } = this.props
        const { activeKey, dx, dy, dcx, dcy } = this.state

        const isBezier = maskObject.type === 'bezier'
        const padding = maskObject.padding || 0

        const width = maskObject.width + 2 * padding
        const height = maskObject.height + 2 * padding

        const circleRadius = ceil(min(width, height) / 40)
        const rectSize = 2 * circleRadius + 2

        const controlPointCursor = `url('${URL.publicPath}/img/cursors/hand-pointer.cur') -8 -8, pointer`

        const lineStyle = {
            strokeWidth: '1px',
            stroke: Theme.steelblue,
        }

        const isDraggable = typeof this.props.updatePoints !== 'undefined'

        const transformX = this.props.transformX || 0
        const transformY = this.props.transformY || 0

        const transformedPoints = maskObject.points ? maskObject.points.map((p: PathPoint | CoordsType | AnyPoint) => {
            return {
                ...p,
                x: p.x + transformX,
                y: p.y + transformY,
                cx: typeof p['cx'] !== 'undefined' ? p['cx'] + transformX : undefined,
                cy: typeof p['cy'] !== 'undefined' ? p['cy'] + transformY : undefined,
                cx2: typeof p['cx2'] !== 'undefined' ? p['cx2'] + transformX : undefined,
                cy2: typeof p['cy2'] !== 'undefined' ? p['cy2']+ transformY : undefined,
            }
        }) : []

        const pointsData = isBezier ? getSVGPathFromPoints(transformedPoints) : ''

        return (
            <React.Fragment>
                {isDraggable &&
                    <React.Fragment>
                        <rect
                            ref={this.rectRef}
                            width={width}
                            height={height}
                            fill={'none'}
                            style={{
                                pointerEvents: 'none',
                            }}
                            x={maskObject.x - .5 * width}
                            y={maskObject.y - .5 * height}
                            transform={`rotate(${-(this.props.newPointAngle || 0)})`}
                        />
                        <rect
                            ref={this.addPointRectRef}
                            width={width}
                            height={height}
                            fill={'transparent'}
                            style={{
                                cursor: 'copy',
                                pointerEvents: 'fill',
                                transformOrigin: `${.5 * width}px ${.5 * height}px`,
                            }}
                            x={maskObject.x - .5 * width}
                            y={maskObject.y - .5 * height}
                        />
                    </React.Fragment>
                }
                {maskObject.type === 'bezier' &&
                    <path
                        key={`l${pointsData}`}
                        d={pointsData}
                        strokeLinejoin={'round'}
                        strokeDasharray={dashArray}
                        strokeWidth={strokeWidth}
                        stroke={strokeColor}
                        style={{ pointerEvents: 'stroke' }}
                        fill={fillColor}
                        ref={this.props.ref}
                        transform={`translate(${maskObject.x}, ${maskObject.y}) rotate(${maskObject.angle || 0})`}
                    />
                }
                {maskObject.type === 'polygon' &&
                    <polygon
                        points={maskObject.points.map((point, key) => {
                            const addX = activeKey === key ? dx : 0
                            const addY = activeKey === key ? dy : 0
                            const xpos = point.x + addX
                            const ypos = point.y + addY
                            return `${xpos},${ypos}`
                        }).join(' ')}
                        strokeLinejoin={maskObject.lineJoin ? maskObject.lineJoin : DEFAULT_LINE_JOIN}
                        strokeLinecap={maskObject.lineCap ? maskObject.lineCap : DEFAULT_LINE_CAP}
                        fill={fillColor}
                        strokeDasharray={dashArray}
                        strokeWidth={strokeWidth}
                        stroke={strokeColor}
                        style={{ pointerEvents: 'stroke' }}
                        ref={this.props.ref}
                        transform={`translate(${maskObject.x}, ${maskObject.y}) rotate(${maskObject.angle || 0})`}
                    />
                }

                {isDraggable && maskObject.points.map((point, key) => {
                    const isActive = activeKey === key

                    const addX = isActive ? dx : 0
                    const addY = isActive ? dy : 0

                    const xpos = point.x + addX
                    const ypos = point.y + addY

                    const cx = point.cx + (isActive ? dcx : 0)
                    const cy = point.cy + (isActive ? dcy : 0)

                    const cx2 = 2 * xpos - cx
                    const cy2 = 2 * ypos - cy

                    return <g key={`pp${key}`}>
                        {isBezier &&
                            <React.Fragment>
                                <line x1={cx} y1={cy} x2={xpos} y2={ypos} style={lineStyle} strokeDasharray={dashArray} />
                                <rect
                                    key={`c1bezier${activeKey}${key}`}
                                    transform={`translate(${cx - rectSize / 2},${cy - rectSize / 2})`}
                                    width={rectSize}
                                    height={rectSize}
                                    fill={'#58839d8f'}
                                    strokeWidth={rectSize / 4}
                                    stroke={isActive ? Theme.blue : 'transparent'}
                                    style={{ cursor: controlPointCursor, pointerEvents: 'auto' }}
                                    className={'handles'}
                                    ref={this.getHandleRef('c', key)}
                                />

                                <line x1={cx2} y1={cy2} x2={xpos} y2={ypos} style={lineStyle} strokeDasharray={dashArray} />
                                <rect
                                    key={`c2bezier${activeKey}${key}`}
                                    transform={`translate(${cx2 - rectSize / 2},${cy2 - rectSize / 2})`}
                                    width={rectSize}
                                    height={rectSize}
                                    fill={'#58839d8f'}
                                    strokeWidth={rectSize / 4}
                                    stroke={isActive ? Theme.blue : 'transparent'}
                                    style={{ cursor: controlPointCursor, pointerEvents: 'auto' }}
                                    className={'handles'}
                                    ref={this.getHandleRef('c2', key)}
                                />
                            </React.Fragment>}

                        <circle
                            key={`p${key}`}
                            transform={`translate(${xpos},${ypos})`}
                            r={circleRadius}
                            fill={isActive ? Theme.red : Theme.steelblue}
                            strokeWidth={2}
                            stroke={Theme.grey}
                            ref={this.getHandleRef('m', key)}
                        />
                        <text
                            dominantBaseline={"central"}
                            textAnchor={"middle"}
                            fill={'#ffffff'}
                            transform={`translate(${xpos},${ypos})`}
                            style={{
                                pointerEvents: 'none',
                                fontSize: `${circleRadius}px`,
                                fontWeight: 'bold',
                            }}
                        >{key}</text>
                    </g>
                })}
            </React.Fragment>
        )
    }
}


export default MoveablePoints